import { Box, type BoxProps, HStack, Stack } from "@chakra-ui/react";
import { Trans } from "@lingui/macro";
import { NotificationType } from "@src/__generated__/graphql";
import type {
  TaskNotification,
  TaskNotificationScoped,
} from "@src/components/modules/notifications/listing/store";
import { IconCommentOutline } from "@src/components/ui-kit/TaskItem/IconComment";
import { TaskItemNotification } from "@src/components/ui-kit/TaskItem/TaskItemNotification";
import { useStore } from "@src/utils/hooks";
import { makeHTMLSafe } from "@src/utils/security/makeHTMLSafe";
import { formatDistanceToNow } from "date-fns";
import { observer } from "mobx-react-lite";
import type React from "react";
import type { FC } from "react";

interface TaskListItemProps extends BoxProps {
  task:
    | NonNullable<TaskNotification["task"]>
    | NonNullable<TaskNotificationScoped["task"]>;
  notifications: NonNullable<TaskNotification["notifications"]>;
}

export const TaskListItem: FC<React.PropsWithChildren<TaskListItemProps>> =
  observer(function TaskListItem({ task, notifications, ...props }) {
    const { taskDetailModalStore } = useStore();
    const icon = (
      notification: NonNullable<TaskNotification["notifications"]>[0],
    ) => {
      switch (notification.type) {
        case NotificationType.TaskCommentAdded:
          return (
            <IconCommentOutline
              width="16"
              height="16"
              className="text-inkLighter"
            />
          );
        case NotificationType.TaskAttachmentAdded:
          return (
            <svg
              width="17"
              height="16"
              viewBox="0 0 17 16"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <title>
                <Trans>Task attachment added</Trans>
              </title>
              <path
                fillRule="evenodd"
                clipRule="evenodd"
                d="M2.09961 3C2.09961 1.39838 3.39798 0.1 4.99961 0.1H6.14247C6.63952 0.1 7.04247 0.502944 7.04247 1C7.04247 1.49706 6.63952 1.9 6.14247 1.9H4.99961C4.3921 1.9 3.89961 2.39249 3.89961 3V13C3.89961 13.6075 4.3921 14.1 4.99961 14.1H11.9996C12.6071 14.1 13.0996 13.6075 13.0996 13V4.11111C13.0996 3.61405 13.5026 3.21111 13.9996 3.21111C14.4967 3.21111 14.8996 3.61405 14.8996 4.11111V13C14.8996 14.6016 13.6012 15.9 11.9996 15.9H4.99961C3.39798 15.9 2.09961 14.6016 2.09961 13V3ZM7.4 8.8V4C7.4 3.5576 7.7584 3.2 8.2 3.2C8.6416 3.2 9 3.5576 9 4V8.8C9.0096 9.16 9.156 9.6 9.8 9.6C10.444 9.6 10.5904 9.16 10.6 8.7904V2.4C10.5904 2.04 10.444 1.6 9.8 1.6C9.3584 1.6 9 1.2424 9 0.8C9 0.3576 9.3584 0 9.8 0C11.5608 0 12.2 1.4352 12.2 2.4V8.8C12.2 9.7648 11.5608 11.2 9.8 11.2C8.0392 11.2 7.4 9.7648 7.4 8.8Z"
                fill="#637381"
              />
            </svg>
          );
        case NotificationType.TaskCommentMentionedUser:
          return (
            <svg
              width="16"
              height="16"
              viewBox="0 0 16 16"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <title>
                <Trans>Task comment mentioned</Trans>
              </title>
              <path
                d="M8.20144 16C9.28537 16 10.3309 15.8561 11.1271 15.6067V14.1871C10.5707 14.4173 9.42926 14.5707 8.29736 14.5707C4.21103 14.5707 1.61151 12.0384 1.61151 8.06715V8.04796C1.61151 4.15348 4.23022 1.41007 7.95204 1.41007C11.6643 1.41007 14.1487 3.72182 14.1487 7.17506V7.19424C14.1487 9.30456 13.4772 10.6667 12.4125 10.6667C11.789 10.6667 11.4341 10.2638 11.4341 9.59233V4.27818H9.67866V5.42926H9.506C9.1319 4.58513 8.28777 4.07674 7.27098 4.07674C5.33333 4.07674 4 5.67866 4 8V8.01919C4 10.446 5.33333 12.0767 7.32854 12.0767C8.44125 12.0767 9.25659 11.5492 9.65947 10.5803H9.83213L9.84173 10.6379C10.0336 11.5396 10.8969 12.1247 12.048 12.1247C14.3309 12.1247 15.7602 10.1871 15.7602 7.11751V7.09832C15.7602 2.89688 12.6139 0 8.03837 0C3.27098 0 0 3.26139 0 7.99041V8.00959C0 12.8537 3.22302 16 8.20144 16ZM7.70264 10.4556C6.60911 10.4556 5.93765 9.55396 5.93765 8.07674V8.05755C5.93765 6.56115 6.59952 5.66906 7.71223 5.66906C8.84412 5.66906 9.55396 6.58993 9.55396 8.05755V8.07674C9.55396 9.53477 8.83453 10.4556 7.70264 10.4556Z"
                fill="#637381"
              />
            </svg>
          );
        default:
          return undefined;
      }
    };

    const displayNotifications = notifications.map((notification) => {
      const date = notification.created_at
        ? formatDistanceToNow(new Date(notification.created_at), {
            addSuffix: true,
          })
        : undefined;

      return (
        <HStack key={notification.id} justify="space-between" spacing="4">
          <HStack flex="1">
            {icon(notification)}
            <span className="text-inkDarker">
              {makeHTMLSafe(notification.message)
                .replaceAll("<strong>", "")
                .replaceAll("</strong>", "")}
            </span>
          </HStack>
          <div className="flex-shrink text-gray-500">{date}</div>
        </HStack>
      );
    });

    return (
      <Box px="3" py="2" borderColor="grey.100" {...props}>
        <TaskItemNotification
          task={{
            ...task,
            deadline: task.deadline ? task.deadline : undefined,
          }}
          notificationsCount={notifications.length}
          hasUnreadNotifications={!notifications[0].read_at}
          onClick={({ id }) =>
            taskDetailModalStore.disclosureState.onOpen({ id })
          }
        />
        <Stack direction="column" px="5" py="3" spacing="3">
          {displayNotifications}
        </Stack>
      </Box>
    );
  });
